import React, { useMemo, useState } from "react";
import {
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";
import { MoreHorizontal } from "lucide-react";
import { ListItemAction } from "@components/ListItemCardCst/types";
import { ButtonIconOnly } from "src/UI/ButtonIconOnly";

interface MoreActionsMenuCstProps {
  actions: ListItemAction[] | ListItemAction[][];
  iconButtonSx?: SxProps<Theme>;
  ariaLabel?: string;
}

const MoreActionsMenuCst: React.FC<MoreActionsMenuCstProps> = ({
  actions,
  iconButtonSx,
  ariaLabel = "more actions",
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const groups = useMemo<ListItemAction[][]>(() => {
    if (!actions || actions.length === 0) return [];
    const isGrouped = Array.isArray(actions[0]);
    return (isGrouped ? actions : [actions]) as ListItemAction[][];
  }, [actions]);

  if (groups.length === 0) return null;

  const handleClose = () => setAnchorEl(null);
  const handleAction = (onClick: () => void) => {
    handleClose();
    onClick();
  };

  return (
    <>
      <ButtonIconOnly
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        aria-label={ariaLabel}
        iconOnly={<MoreHorizontal size={18} />}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {groups.map((group, groupIndex) => [
          groupIndex > 0 && (
            <Divider key={`divider-${groupIndex}`} sx={{ my: "4px" }} />
          ),
          ...group.map((action, actionIndex) => (
            <MenuItem
              key={`${groupIndex}-${actionIndex}`}
              disabled={action.disabled}
              onClick={() => handleAction(action.onClick)}
              sx={{
                fontSize: "13px",
                color: theme.palette.text.primary,
                gap: "10px",
              }}
            >
              {action.icon && (
                <ListItemIcon
                  sx={{
                    minWidth: "auto !important",
                    color: theme.palette.text.primary,
                  }}
                >
                  {action.icon}
                </ListItemIcon>
              )}
              {action.label}
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  );
};

export default MoreActionsMenuCst;
